import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { showErrorNotification } from '../../_shared/views/Notifications';
import { LONG_POLLING_INTERVAL } from '../../constants';
import { useSseWithPollingFallback } from '../../utils/sse-or-polling';
import { User, UsersApi } from '../users/users-api';
import { collapseUsers } from '../users/utils';
import { WbService, serviceFromData } from './models/service';
import { ServicesApi, SharePermissions, WbServiceData } from './services-api';

const api = new ServicesApi();
const usersApi = new UsersApi();

export function useServicesSSE() {
  const queryClient = useQueryClient();

  function toServices(data: WbServiceData[] | undefined) {
    const existing = queryClient.getQueryData<WbService[]>(['services']) ?? [];
    return (data ?? []).map((service) => serviceFromData(existing, service));
  }

  const { data: services = [], isLoading } = useSseWithPollingFallback<WbService[]>({
    queryKey: ['services'],
    subscribeSSE: () =>
      api.getUserServicesSSE((data) => {
        queryClient.setQueryData(['services'], toServices(data));
      }),
    fetchFallbackFn: async () => toServices(await api.getUserServices()),
    refetchInterval: LONG_POLLING_INTERVAL,
  });

  return { services, isLoading };
}

export function useDeployService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, shortId }: WbService) => api.deployService(shortId, id),
    onMutate: (service) => {
      queryClient.setQueryData<WbService[]>(['services'], (prev) =>
        prev?.map((s) => (s.id === service.id ? { ...s, statusOverride: 'PROVISIONING' } : s))
      );
    },
    onSuccess: (res) => {
      if (!res?.ok) {
        showErrorNotification({ message: res?.error ?? 'Error deploying a service', deduplicationKey: 'error-deploying-service' });
      }
      void queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}

export function useStopService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, shortId }: WbService) => api.stopService(shortId, id),
    onMutate: (service) => {
      queryClient.setQueryData<WbService[]>(['services'], (prev) =>
        prev?.map((s) => (s.id === service.id ? { ...s, statusOverride: 'STOPPING' } : s))
      );
    },
    onSuccess: (res) => {
      if (!res?.ok) {
        showErrorNotification({ message: 'Error stopping app', deduplicationKey: 'error-stopping-service' });
      }
      void queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}

export function useDeleteService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, shortId }: WbService) => api.deleteService(shortId, id),
    onMutate: (service) => {
      queryClient.setQueryData<WbService[]>(['services'], (prev) =>
        prev?.map((s) => (s.id === service.id ? { ...s, statusOverride: 'REMOVING' } : s))
      );
    },
    onSuccess: (res) => {
      if (!res?.ok) {
        showErrorNotification({ message: 'Error removing app', deduplicationKey: 'error-removing-service' });
      }
      void queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}

export function useUninstallService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: WbService) => api.uninstallService(id),
    onMutate: (service) => {
      queryClient.setQueryData<WbService[]>(['services'], (prev) =>
        prev?.map((s) => (s.id === service.id ? { ...s, statusOverride: 'UNINSTALLING' } : s))
      );
    },
    onSuccess: (res) => {
      if (!res?.ok) {
        showErrorNotification({ message: 'Error uninstalling app', deduplicationKey: 'error-uninstalling-service' });
      }
      void queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}

export function useServiceAuthorizedUsers(service: WbService) {
  return useQuery({
    queryKey: ['service-authorized-users', service.id, service.shortId],
    queryFn: async (): Promise<User[]> => {
      if (!service.shortId) {
        return collapseUsers((await usersApi.getUsers())?.allUserGroups ?? []);
      }
      const users = (await api.getAuthorizedUsers(service.shortId)) ?? [];
      return users.map((u) => ({ name: u.name, value: u.id, email: u.email, title: `${u.name} (${u.email})`, selectable: true }));
    },
    initialData: [],
  });
}

export function useShareService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ service, permissions }: { service: WbService; permissions: SharePermissions }) =>
      api.shareService(service.shortId, service.id, permissions),
    onMutate: ({ service, permissions }) => {
      queryClient.setQueryData<WbService[]>(['services'], (prev) => prev?.map((s) => (s.id === service.id ? { ...s, permissions } : s)));
    },
    onSuccess: (res) => {
      if (!res?.ok) {
        showErrorNotification({ message: 'Error sharing app', deduplicationKey: 'error-sharing-service' });
      }
      void queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}
